import {Map, List} from 'immutable';
import {Matchable, TopicId, TopicFilter} from '@store/topic-keyword/TopicKeyword.types';
import {Color} from '@store/@types/util';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import CallCardActions from '@store/call-card/CallCardActions';
import {Speaker, TopicMatch} from '@store/call-card/CallCard.types';

export const TOPIC_COLORS = [
  // color : text color
  '#7C4DFF',
  '#FFD740',
  '#E040FB',
  '#69F0AE',
  '#536DFE',
  '#B2FF59',
  '#FF6E40',
  '#40C4FF',
];

export const TOPIC_CONTRAST_TEXT = {
  // color : text color
  '#7C4DFF': '#fff',
  '#FFD740': '#000',
  '#E040FB': '#fff',
  '#69F0AE': '#000',
  '#536DFE': '#fff',
  '#B2FF59': '#000',
  '#FF6E40': '#fff',
  '#40C4FF': '#000',
};

const defaultCount = Map<Color, number>({
  '#7C4DFF': 0,
  '#FFD740': 0,
  '#E040FB': 0,
  '#69F0AE': 0,
  '#536DFE': 0,
  '#B2FF59': 0,
  '#FF6E40': 0,
  '#40C4FF': 0,
});

/**
 * Return the next color to assign when activating topics
 */
export function getNextColor(activeTopics: Map<TopicId, Color>): Color {
  const colorCount = activeTopics.reduce(
    (acc, color) => acc.update(color, count => count + 1),
    defaultCount
  );

  const lowestCount = colorCount.toList().min();
  return (
    colorCount
      // Colors with the lowest count
      .filter(count => count === lowestCount)
      .keySeq()
      .toList()
      // Sort by precedence (order in the list above)
      .sortBy(color => TOPIC_COLORS.findIndex(c => color === c))
      // take the first
      .first()
  );
}

export function togglePill(callId: FileUploadId, topic: Matchable, active: boolean) {
  CallCardActions.toggleTopic(callId, topic.id);
  // If we're going from inactive to active, then get the matches
  if (!active) {
    CallCardActions.getMatches(callId, topic).catch(console.error);
  }
}

export function filterTopics(
  topicMatches: Map<string, List<TopicMatch>>,
  speakers: List<Speaker>,
  filter: TopicFilter
) {
  if (filter === TopicFilter.EITHER) {
    return topicMatches;
  }

  let filteredTopicMatches: Map<string, List<TopicMatch>> = Map();

  topicMatches.keySeq().forEach(key => {
    const filteredMatches = topicMatches.get(key)!.filter(match => {
      const speaker = speakers.find(s => s.speakerNumber === match.speakerNumber);
      return doesSpeakerMatchFilter(speaker, filter);
    });
    filteredTopicMatches = filteredTopicMatches.update(key, () => filteredMatches);
  });

  return filteredTopicMatches;
}

export function doesSpeakerMatchFilter(speaker: Speaker | undefined, filter: TopicFilter) {
  // Speakers with userId is internal, without userId is external or unidentified
  return (
    filter === TopicFilter.EITHER ||
    (speaker !== undefined && speaker.userId && filter === TopicFilter.INTERNAL) ||
    (speaker !== undefined && !speaker.userId && filter === TopicFilter.EXTERNAL)
  );
}
